/**
 * AEO Elements Library Component
 *
 * Displays all 13 AEO elements organized by group with toggles and presets.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { AEOElement } from '@/services/aeoDemoService';

interface AEOElementsLibraryProps {
  elements: AEOElement[];
  selectedIds: string[];
  onToggle: (elementId: string) => void;
  onPresetSelect: (preset: string) => void;
  onClear: () => void;
  activePreset: string | null;
  disabled?: boolean;
}

const PRESET_LABELS: Record<string, { label: string; description: string }> = {
  minimal: { label: 'Minimal', description: '2 elements: Summary + FAQ' },
  standard: { label: 'Standard', description: '4 elements: Core AEO content' },
  comprehensive: { label: 'Comprehensive', description: '7 elements: Extended coverage' },
  full: { label: 'Full', description: 'All 13 elements' },
};

const GROUP_LABELS: Record<string, string> = {
  overview: 'Overview',
  problem_solution: 'Problem/Solution',
  buyer_guidance: 'Buyer Guidance',
  product_details: 'Product Details',
  qa: 'Q&A',
};

const GROUP_ORDER = ['overview', 'problem_solution', 'buyer_guidance', 'product_details', 'qa'];

export function AEOElementsLibrary({
  elements,
  selectedIds,
  onToggle,
  onPresetSelect,
  onClear,
  activePreset,
  disabled = false,
}: AEOElementsLibraryProps) {
  // Group elements
  const groupedElements = GROUP_ORDER.map(groupId => ({
    id: groupId,
    label: GROUP_LABELS[groupId],
    elements: elements.filter(e => e.group === groupId),
  }));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">AEO Elements</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {selectedIds.length} / {elements.length}
            </Badge>
            {selectedIds.length > 0 && (
              <Button variant="ghost" size="sm" onClick={onClear} disabled={disabled}>
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Presets */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(PRESET_LABELS).map(([key, { label, description }]) => (
            <TooltipProvider key={key}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={activePreset === key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onPresetSelect(key)}
                    disabled={disabled}
                  >
                    {label}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>

        {/* Elements by group */}
        <div className="space-y-4">
          {groupedElements.map(group => (
            <div key={group.id}>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">
                {group.label}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {group.elements.map(element => (
                  <TooltipProvider key={element.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'flex items-center justify-between p-2 rounded border transition-colors',
                            element.available
                              ? 'bg-background hover:bg-muted/50'
                              : 'bg-muted/30 opacity-50',
                            selectedIds.includes(element.id) && 'border-primary bg-primary/5'
                          )}
                        >
                          <span className={cn(
                            'text-sm truncate mr-2',
                            !element.available && 'text-muted-foreground'
                          )}>
                            {element.label}
                          </span>
                          <Switch
                            checked={selectedIds.includes(element.id)}
                            onCheckedChange={() => onToggle(element.id)}
                            disabled={disabled || !element.available}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="text-sm">{element.preview}</p>
                        {element.available && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {element.wordCount} words
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default AEOElementsLibrary;
